import {
  useMeasureTextWithTypographyVariant,
  VIRTUALIZED_AUTOCOMPLETE_ESTIMATED_ROW_HEIGHT,
} from '@chronosphereio/chrono-ui';

type UseTimeRangeSelectLayoutProps = {
  optionsCount: number;
  inputValue: string;
  lastLoadedValue: string;
  timeZoneValue: string;
  longestDisplayValue: string;
  showInputError: boolean;
};

type UseTimeRangeSelectLayoutResponse = {
  /**
   * Width to use on the overall select.
   */
  selectWidth: number;
  /**
   * Width to use for the listbox that contains options.
   */
  listboxWidth: number;
  /**
   * Height to use for the listbox that contains options.
   */
  listboxHeight: number;
};

/**
 * Calculate some dynamic layout for the time range select.
 */
export function useTimeRangeSelectLayout({
  inputValue,
  lastLoadedValue,
  timeZoneValue,
  longestDisplayValue,
  showInputError,
  optionsCount,
}: UseTimeRangeSelectLayoutProps): UseTimeRangeSelectLayoutResponse {
  // Fallback widths should be very rare because all of our supported browsers support canvas.
  const inputValueWidth = useMeasureTextWithTypographyVariant(inputValue, 'body1')?.width ?? 100;
  const longestOptionWidth = useMeasureTextWithTypographyVariant(longestDisplayValue, 'body1')?.width ?? 115;
  const lastLoadedWidth =
    useMeasureTextWithTypographyVariant(lastLoadedValue ? `${lastLoadedValue}` : '', 'body2')?.width ?? 0;
  const timeZoneWidth = useMeasureTextWithTypographyVariant(timeZoneValue, 'body2')?.width ?? 0;

  // Somewhat arbitrary minimum input width, which ensures the input stays a reasonable size if
  // the user clears the input value.
  const MIN_INPUT_WIDTH = 60;
  // Approximated space needed for decorations, padding, etc. on top of the space the input text
  // takes up. Using this value in our calculations, so we can set the width on the overall Select
  // and not just on the input, which simplifies keeping the dropdown options and the select width
  // synced. It would be difficult to calculate this value on the fly, so we will have to manually
  // update it if something about the layout changes. Visual tests should help us catch these cases
  // if they occur.
  const SELECT_PADDING = 39 + 9;
  const SELECT_START_ICON = 18;
  const SELECT_INPUT_PADDING = 6 + 4;
  const SELECT_BORDER = 1 * 2;
  // Padding + separator
  const LAST_LOADED_SEPARATOR = lastLoadedWidth ? 4 + 4 : 0;
  const TIME_ZONE_PADDING = timeZoneWidth ? 4 : 0;
  const END_ADORNMENT_PADDING = lastLoadedWidth || timeZoneWidth ? 4 : 0;
  const SELECT_EXTRA_WIDTH =
    SELECT_BORDER +
    SELECT_PADDING +
    SELECT_START_ICON +
    SELECT_INPUT_PADDING +
    END_ADORNMENT_PADDING +
    LAST_LOADED_SEPARATOR +
    TIME_ZONE_PADDING;
  // Use Math.ceil to avoid some annoying edge cases around fractional pixels from text measurements.
  const selectWidth = Math.ceil(
    Math.max(inputValueWidth, MIN_INPUT_WIDTH) + lastLoadedWidth + timeZoneWidth + SELECT_EXTRA_WIDTH
  );

  // Arbitrary value selected for the width to show when we include the error message. We do not
  // use the same logic as other options (where we calculate the width of the text) because the
  // error message is very long, so we want it to wrap.
  const ERROR_OPTION_WIDTH = 300;
  const OPTION_PADDING = 16 * 2;
  const APPROX_SCROLLBAR_WIDTH = 16;
  const initListboxWidth = showInputError
    ? ERROR_OPTION_WIDTH
    : longestOptionWidth + OPTION_PADDING + APPROX_SCROLLBAR_WIDTH;
  // The listbox width will match the select unless it needs to be larger to render items without
  // wrapping.
  const listboxWidth = Math.max(initListboxWidth, selectWidth);

  // Attempt to use this to override the default in `Select`, which will max out at 10 rows.
  // Note: that this is also constrained by a vh-based maxHeight for the popper in the MUI
  // autocomplete, so this height may not be respected on shorter screens.
  const listboxHeight = optionsCount * VIRTUALIZED_AUTOCOMPLETE_ESTIMATED_ROW_HEIGHT;

  return {
    selectWidth,
    listboxWidth,
    listboxHeight,
  };
}
